import { Building2, Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { RefObject } from "react";
import { Button } from "./ui/button";
import { ThemeToggle } from "./theme-toggle";
import { LanguageToggle } from "./language-toggle";
import { usePageHeaderState } from "../lib/use-page-header";
import { useBranch } from "../lib/use-branch";
import { type Tab } from "../routing";

interface TopbarProps {
  tab: Tab;
  onMenu: () => void;
  // Desktop sidebar yig'ilganmi + uni almashtirish. Mobil `onMenu` drawer'ni
  // ochadi — ikkalasi bir slotda, breakpoint bo'yicha ajratilgan.
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  menuTriggerRef?: RefObject<HTMLButtonElement>;
}

// Global filial selektori faqat shu tablarni filtrlaydi (qarz emas — qarz
// daftari ustaxona darajasida). Mijozlarda "shu filialda tashrifi borlar".
const BRANCH_FILTERABLE: Tab[] = ["home", "works", "clients", "analytics", "tovars", "foyda_zarar"];

export function Topbar({
  tab,
  onMenu,
  sidebarCollapsed,
  onToggleSidebar,
  menuTriggerRef,
}: TopbarProps) {
  const { t } = useTranslation("common");
  const { filters, actions } = usePageHeaderState();
  const { branches, selectedBranchId, setBranch, showSelector } = useBranch();
  const showBranch = showSelector && BRANCH_FILTERABLE.includes(tab);

  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b bg-background/95 backdrop-blur px-4 sm:px-6 h-16">
      <Button
        ref={menuTriggerRef}
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenu}
        aria-label={t("topbar.menu")}
      >
        <Menu />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="hidden lg:inline-flex"
        onClick={onToggleSidebar}
        aria-label={t(
          sidebarCollapsed ? "topbar.expandSidebar" : "topbar.collapseSidebar",
        )}
        title={t(
          sidebarCollapsed ? "topbar.expandSidebar" : "topbar.collapseSidebar",
        )}
      >
        {sidebarCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
      </Button>

      <div className="flex items-center gap-2 lg:hidden min-w-0">
        <div className="size-7 rounded-[8px] bg-[#0A0E18] p-[3px] shrink-0">
          <img
            src="/logo-mark.png"
            alt="AvtoDaftar"
            className="w-full h-full object-cover rounded-[5px]"
          />
        </div>
        <span className="font-num font-bold text-sm truncate">
          {t(`tabs.${tab}.title`)}
        </span>
      </div>

      <div className="hidden lg:flex flex-col leading-tight min-w-0">
        <h1 className="font-num text-[19px] font-bold tracking-tight truncate">
          {t(`tabs.${tab}.title`)}
        </h1>
        <p className="text-[12.5px] text-muted-foreground truncate">
          {t(`tabs.${tab}.subtitle`)}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2 min-w-0">
        {showBranch && (
          <div className="flex items-center gap-1.5">
            <Building2 className="size-4 text-muted-foreground hidden sm:block" />
            <select
              aria-label={t("topbar.branchLabel")}
              className="ui-control h-9 rounded-md border border-input bg-background px-2 text-sm max-w-[160px]"
              value={selectedBranchId ?? ""}
              onChange={(e) =>
                setBranch(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">{t("topbar.branchAll")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {filters}
        {actions}
        <LanguageToggle />
        <ThemeToggle />
      </div>
    </header>
  );
}
